/**
 * What Sentry is told about this build, and whether it is told anything at all.
 *
 * Both questions used to be answered inline at the `Sentry.init` call, and both
 * were answered wrongly (MYFITWORLD-V2-69):
 *
 *   - the gate was `NODE_ENV !== 'local'`, which asks about the BUILD MODE. A
 *     production build served from a local preview server therefore reported
 *     into the production project — nine events and two "affected users" that
 *     were one developer and one sandbox.
 *   - `environment` was never passed, and the SDK defaults it to `production`.
 *     Every event from the dev deployment is filed as production, so an alert
 *     or filter scoped to real traffic matches the team's own testing. The
 *     rspack build goes to some length to bake `APP_ENV` into the bundle and
 *     refuses to build at all without a valid one; only this half was missing.
 *
 * Kept as plain functions over plain inputs so both answers are pinned by tests
 * without loading the SDK or the bundler globals.
 */

// Hosts that mean "this is my machine": a build running here is being looked at
// directly, in a console with source maps, by the person who built it.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '::1', '0.0.0.0'];

export const shouldInitSentry = ({
  dsn,
  hostname,
  isLocalBuild = false,
}: {
  dsn: string;
  hostname: string;
  // `APP_ENV === 'local'`, i.e. the dev server's own build. Kept as a second
  // question because the host answers only half of it: `pnpm dev` bound to
  // 0.0.0.0 and opened over a LAN address looks deployed and is not.
  isLocalBuild?: boolean;
}) =>
  Boolean(dsn) &&
  Boolean(hostname) &&
  !isLocalBuild &&
  !LOCAL_HOSTS.includes(hostname);

export interface SentryRuntimeConfig {
  dsn: string;
  env: string;
  release: string;
  tracePropagationTargets: string[];
}

// An unlabelled build is labelled as unlabelled. The one value this must never
// fall back to is `production`, which is exactly what the SDK does on its own.
const UNSPECIFIED_ENVIRONMENT = 'unspecified';

const PRODUCTION_LABELS = ['production', 'prod'];

// Errors thrown by the BROWSER's own scripts, which no change of ours can fix.
// Each one exact, so an error of the same shape from our code still reports.
const BROWSER_NOISE: RegExp[] = [
  // V2-7D: WebKit's modern-media-controls read a buffered range on a <video>
  // that just failed. Ours is the "Video playback failed" report beside it.
  /Can't find variable: EmptyRanges$/,
];

export const buildSentryOptions = ({
  dsn,
  env,
  release,
  tracePropagationTargets,
}: SentryRuntimeConfig) => {
  const environment = env || UNSPECIFIED_ENVIRONMENT;
  // `APP_ENV` is a GitHub variable someone types, and the cost of reading it
  // too strictly falls on the environment that matters: a `prod` label would
  // silently sample real users down to a tenth.
  const isProduction = PRODUCTION_LABELS.includes(environment.toLowerCase());

  return {
    dsn,
    environment,
    ignoreErrors: BROWSER_NOISE,
    // Everything that fails is worth a replay, in every environment — that is
    // what turned a one-off Safari crash into a reproducible one. Recording
    // sessions that DON'T fail is the expensive half, and outside production
    // the sessions are our own.
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: isProduction ? 0.1 : 0,
    // `release: ''` is worse than no release: it would override the value the
    // Sentry bundler plugin injects and orphan the uploaded source maps.
    ...(release ? { release } : {}),
    sendDefaultPii: true,
    tracePropagationTargets,
    // Outside production every transaction is traffic we generate ourselves.
    tracesSampleRate: isProduction ? 1 : 0.1,
  };
};
